/// Emergency roll + countdown logic. Mirrors the slim slice of
/// FlatoutATC/Support/AircraftSpawner.swift that decides whether a
/// new aircraft starts with an emergency, and the iOS countdown
/// formula based on time-to-nearest-airport.
///
/// Emergencies in the web MVP are landing-only (no in-flight
/// declarations). They're rolled at spawn time for aircraft whose
/// goal is `land`; takeoff/transition aircraft don't get them.

import type { Aircraft, EmergencyReason, EmergencyState } from './Aircraft'
import type { Airport } from '../types/LevelData'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type { RandomSource } from './rng'
import { pick } from './rng'

const REASONS: readonly EmergencyReason[] = [
  'birdStrike',
  'lowFuel',
  'engineFailure',
  'vfrInIfr',
  'medicalEmergency',
  'pressureLoss',
] as const

const MIN_COUNTDOWN_SEC = 60
const MAX_COUNTDOWN_SEC = 2400
/// Buffer multiplier on time-to-airport — gives the player some
/// breathing room above the bare-minimum direct-flight time.
const COUNTDOWN_BUFFER = 8

/// Returns a fresh EmergencyState for the given aircraft, scaled to
/// how far it is from its target airport. Returns null if there's no
/// reasonable target (no airports, or speed is zero).
export function rollEmergency(args: {
  aircraft: Aircraft
  airports: Airport[]
  projector: CoordinateProjector
  pxPerNM: number
  rng: RandomSource
}): EmergencyState | null {
  const { aircraft, airports, projector, pxPerNM, rng } = args
  if (airports.length === 0 || aircraft.speedKts <= 0) return null

  // Use the aircraft's target airport when available (goal=land), else
  // the closest one. iOS picks `level.airports.first` unconditionally.
  const target =
    (aircraft.goal.land
      ? airports.find((a) => a.name === aircraft.goal.land?.airportName)
      : null) ?? airports[0]
  if (!target) return null

  const ap = projector.project(target.centerCoordinate, true)
  const dx = ap.x - aircraft.position.x
  const dy = ap.y - aircraft.position.y
  const distNM = Math.hypot(dx, dy) / pxPerNM
  const timeToAirportSec = (distNM / aircraft.speedKts) * 3600 * COUNTDOWN_BUFFER
  const countdownSec = Math.max(MIN_COUNTDOWN_SEC, Math.min(MAX_COUNTDOWN_SEC, timeToAirportSec))

  const reason = pick(rng, REASONS) ?? 'lowFuel'
  return { reason, countdownSec }
}

/// Pretty-print an emergency reason for the command panel / aircraft
/// label.
export function emergencyLabel(reason: EmergencyReason): string {
  switch (reason) {
    case 'birdStrike':
      return 'Bird Strike'
    case 'lowFuel':
      return 'Low Fuel'
    case 'engineFailure':
      return 'Engine Failure'
    case 'vfrInIfr':
      return 'VFR in IFR'
    case 'medicalEmergency':
      return 'Medical'
    case 'pressureLoss':
      return 'Pressure Loss'
  }
}
